// src/hooks/useStompClient.ts
//
// Ported as-is from the old project — no design-system surface here (it's
// pure WebSocket plumbing, nothing rendered), so nothing needed changing
// beyond confirming the one import (@/utils/helper) resolves in this
// project too. All the fixes already baked into this version (single
// connect/disconnect effect instead of the old duplicate, stale-subscription
// replacement instead of silent stacking) are kept.

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState, useEffect, useCallback } from "react";
import { Client, type IMessage, type StompSubscription } from "@stomp/stompjs";
import { getWebSocketUrl } from "@/utils/helper";

interface UseStompClientOptions {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
}

export const useStompClient = (
  token: string | null,
  options: UseStompClientOptions = {},
) => {
  const { onConnect, onDisconnect, onError } = options;
  const clientRef = useRef<Client | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscriptionsRef = useRef<Map<string, StompSubscription>>(new Map());

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.deactivate();
      clientRef.current = null;
      setIsConnected(false);
      subscriptionsRef.current.clear();
    }
  }, []);

  const connect = useCallback(() => {
    if (!token) {
      console.warn(
        "[useStompClient] No auth token found, skipping WebSocket connection",
      );
      return;
    }

    if (clientRef.current?.active) {
      return;
    }

    const brokerURL = getWebSocketUrl();

    const client = new Client({
      brokerURL,
      connectHeaders: { Authorization: `Bearer ${token}` },
      debug: (_str) => {
        // import.meta.env is the Vite-safe equivalent of process.env, which
        // doesn't exist in a browser bundle unless polyfilled.
        if (import.meta.env.DEV) {
          // console.log("STOMP Debug:", _str);
        }
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      onConnect: () => {
        setIsConnected(true);
        onConnect?.();
      },

      onStompError: (frame) => {
        console.error("STOMP Error:", frame);
        setIsConnected(false);
        onError?.(frame);
      },

      onWebSocketClose: (_event) => {
        setIsConnected(false);
        onDisconnect?.();
      },

      onWebSocketError: (event) => {
        console.error("WebSocket error", event);
        setIsConnected(false);
        onError?.(event);
      },
    });

    client.activate();
    clientRef.current = client;
  }, [token, onConnect, onDisconnect, onError]);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  const subscribe = useCallback(
    (destination: string, callback: (message: IMessage) => void) => {
      const client = clientRef.current;
      if (!client || !client.connected) {
        console.warn(
          `Cannot subscribe to ${destination}: WebSocket not connected`,
        );
        return undefined;
      }

      const existing = subscriptionsRef.current.get(destination);
      if (existing) {
        existing.unsubscribe();
      }

      try {
        const subscription = client.subscribe(destination, callback);
        subscriptionsRef.current.set(destination, subscription);
        return subscription;
      } catch (error) {
        console.error(`Error subscribing to ${destination}:`, error);
        return undefined;
      }
    },
    [],
  );

  const sendMessage = useCallback((destination: string, body: any) => {
    const client = clientRef.current;
    if (!client || !client.connected) {
      console.warn("Cannot send message: WebSocket not connected");
      return;
    }
    try {
      client.publish({
        destination,
        body: JSON.stringify(body),
      });
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  }, []);

  return {
    client: clientRef.current,
    isConnected,
    subscribe,
    sendMessage,
    reconnect: () => {
      disconnect();
      setTimeout(connect, 100);
    },
  };
};
